import { REPORT_LIMITS, checkReportRange, type ReportResult, type UsageRow } from "@fts/core";
import { useMemo, useState } from "react";
import { HelpTip } from "../../components/HelpTip";
import { PageHeader, Panel, EmptyState } from "../../components/ui";
import { useI18n } from "../../i18n";
import { formatNum, formatPct, coverageOf } from "../../lib/report-figures";
import { durationLabel, operatorText, usageSheet } from "../../lib/report-sheets";
import type { Route } from "../../router/hash";
import { useSession, type TenantData } from "../../state/session";
import { ChartPanel, CoverageNote, PrintBrand, ReportContext, ReportEmpty, SummaryCards, periodText, previousOf } from "./ReportParts";
import { toSummaryLines, usageChart, usageSummary, type UsageChartBy } from "./presenters";
import { ExportBar, FilterBar, Loading, contextChips, filterSummary, parseFilters, rangeKey, scopeVehicles, siteLabel, todayLocal, useLatest, useLoad, settleWithin, PREVIOUS_WAIT_MS, type ExportSpec } from "./shared";
import { loadWorkHours, scopeSiteIds, type WorkHoursInfo } from "./work-hours";

/** Kullanım raporu (ADR 0018): per vehicle and local day — ignition, moving, idle, distance, lifts, utilisation, operators. */
export function UsageReport({ data, route }: { data: TenantData; route: Route }) {
  const { t, locale, lang } = useI18n();
  const { provider, tenant } = useSession();
  const f = parseFilters(route, data);
  const issue = checkReportRange(f.from, f.to);
  const tenantId = tenant?.id;
  const [by, setBy] = useState<UsageChartBy>("day");
  const query = (id: string, range = { from: f.from, to: f.to }) => ({ tenantId: id, siteId: f.siteId, vehicleIds: f.vehicleIds, operatorId: f.operatorId, ...range, basis: f.basis });
  const deps = [provider, tenantId, f.siteId, f.vehicleIds.join(","), f.operatorId, f.from, f.to, f.basis];
  const state = useLoad<ReportResult<UsageRow>>(tenantId && !issue ? () => provider.reportUsage(query(tenantId)) : null, deps);
  // The previous equal period through the same report call (same filters, same RLS): the comparison only.
  const prevRange = previousOf(f);
  // Fired only once the main result is in (never two report calls racing for the same screen).
  const prev = useLoad<ReportResult<UsageRow>>(tenantId && !issue && state.status === "ready" ? () => provider.reportUsage(query(tenantId, prevRange)) : null, [...deps, state.status, state.data]);
  const prevLatest = useLatest(prev);
  const scope = scopeSiteIds(data, f.siteId, f.vehicleIds);
  const hours = useLoad<WorkHoursInfo>(() => loadWorkHours(provider, t, data, scope), [provider, scope.join(",")]);
  const workHours = hours.data ?? null;
  const rows = state.data?.rows ?? [];
  const tz = state.data?.tz ?? null;
  // Operator names are personal data: shown (and exported) only for roles that can see operators (operators RLS).
  const hasOps = (rs: UsageRow[]) => rs.some((r) => r.operatorNames.length > 0 || r.operatorsHidden > 0 || r.operatorsUnknown > 0);
  const withOperators = hasOps(rows);
  const ctx = { t, locale, lang };
  const prevRows = prev.status === "ready" ? (prev.data?.rows ?? null) : null;
  const summary = usageSummary(ctx, rows, prevRows);
  const chart = useMemo(() => usageChart({ t, locale, lang }, rows, by, f.from, f.to), [rows, by, f.from, f.to, t, locale, lang]);
  const vehicles = scopeVehicles(data, f.siteId, f.vehicleIds);
  const coverage = coverageOf({ vehicles, devices: data.devices, from: f.from, to: f.to, tz: tz ?? "Europe/Istanbul", today: todayLocal() });
  const used = new Set(rows.map((r) => r.vehicleId));
  // Only without an operator filter: with one, the other vehicles simply had other drivers.
  const unused = !f.operatorId ? vehicles.filter((v) => !used.has(v.id) && !coverage.silentAll.includes(v.fleetNo)).map((v) => v.fleetNo) : [];
  const num = (v: number, d = 1) => formatNum(v, locale, d);
  const filters = filterSummary(t, data, f, { basis: true, workHours });
  const period = periodText(t, lang, f.from, f.to);
  const chartTitle = by === "day" ? t("reports.usage.chart.titleDay") : t("reports.usage.chart.titleVehicle");
  const spec: ExportSpec | null = state.status === "ready" && state.data && tenantId
    ? {
        report: "usage",
        reportName: t("reports.usage.title"),
        slug: t("reports.usage.slug"),
        part: `${f.from}_${f.to}`,
        tz: state.data.tz,
        filters,
        period,
        rows: rows.length,
        load: async (format) => {
          const [r, p] = await Promise.all([provider.exportUsage(query(tenantId), format), format === "pdf" ? settleWithin(prevLatest.current.promise, PREVIOUS_WAIT_MS) : Promise.resolve(null)]);
          return {
            sheets: [usageSheet(t, r.rows, hasOps(r.rows))],
            tz: r.tz,
            summary: toSummaryLines(ctx, usageSummary(ctx, r.rows, p?.rows ?? null)),
            chart: { title: chartTitle, spec: usageChart(ctx, r.rows, by, f.from, f.to), note: t("reports.usage.chart.note") },
          };
        },
      }
    : null;
  const rk = rangeKey(issue);
  const ready = state.status === "ready";

  return (
    <>
      <PrintBrand title={t("reports.usage.title")} tz={tz} />
      <PageHeader title={t("reports.usage.title")} subtitle={t("reports.usage.subtitle")} eyebrow={t("nav.group.reports")} help={<HelpTip topic={t("help.usage.topic")} align="start">{t("help.usage", { kmh: REPORT_LIMITS.minSpeedKmh, cap: REPORT_LIMITS.gapCapS / 60 })}</HelpTip>} />
      <FilterBar data={data} path="/reports/usage" filters={f} basis workHours={workHours} />
      {rk && (
        <p className="form-error" role="alert">
          {t(rk)}
        </p>
      )}
      <ReportContext chips={contextChips(t, filters)} from={f.from} to={f.to} tz={tz} sample={provider.mode === "demo"} />
      {ready && rows.length > 0 && <SummaryCards items={summary} busy={prev.status === "loading"} prevFrom={prevRange.from} prevTo={prevRange.to} />}
      {ready && <CoverageNote coverage={coverage} tz={tz ?? "Europe/Istanbul"} />}
      {ready && rows.length > 0 && unused.length > 0 && <p className="report-meta">{t("reports.usage.vehiclesWithoutUse", { vehicles: unused.join(", ") })}</p>}
      <ExportBar spec={spec} disabled={rows.length === 0} />
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      {ready && rows.length > 0 && (
        <ChartPanel
          title={chartTitle}
          spec={chart}
          note={t("reports.usage.chart.note")}
          controls={
            <div className="segmented segmented-sm" role="group" aria-label={t("reports.chart.view")}>
              {(["day", "vehicle"] as const).map((x) => (
                <button key={x} type="button" className={x === by ? "seg-on" : ""} aria-pressed={x === by} onClick={() => setBy(x)}>
                  {x === "day" ? t("reports.usage.chart.byDay") : t("reports.usage.chart.byVehicle")}
                </button>
              ))}
            </div>
          }
        />
      )}
      <Panel flush meta={ready ? <span>{t("reports.rows", { count: rows.length })}</span> : undefined}>
        {state.status === "loading" || state.status === "idle" ? (
          rk ? <EmptyState compact icon="info" title={t(rk)} /> : <Loading />
        ) : rows.length === 0 ? (
          state.status === "error" ? null : <ReportEmpty path="/reports/usage" filters={f} siteName={siteLabel(t, data, f.siteId)} keep={{ b: f.basis !== "auto" ? f.basis : undefined }} />
        ) : (
          <div className="table-wrap table-scroll">
            <table className="table report-table">
              <thead>
                <tr>
                  <th scope="col">{t("reports.col.vehicle")}</th>
                  <th scope="col">{t("reports.col.day")}</th>
                  <th scope="col" className="num">{t("reports.col.ignition")}</th>
                  <th scope="col" className="num">{t("reports.col.moving")}</th>
                  <th scope="col" className="num col-hide-sm">{t("reports.col.idle")}</th>
                  <th scope="col" className="num">{t("reports.col.distanceKm")}</th>
                  <th scope="col" className="num col-hide-md">{t("reports.col.lifts")}</th>
                  <th scope="col" className="num">{t("reports.col.utilisation")}</th>
                  {withOperators && <th scope="col" className="col-hide-md">{t("reports.col.operators")}</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.vehicleId}|${r.day}`}>
                    <td>
                      <a className="subtle-link mono" href={`#/vehicles/${encodeURIComponent(r.vehicleId)}`}>
                        {r.fleetNo}
                      </a>
                    </td>
                    <td className="nowrap">{new Date(`${r.day}T12:00:00Z`).toLocaleDateString(locale, { day: "2-digit", month: "short", weekday: "short", timeZone: "UTC" })}</td>
                    <td className="num">{durationLabel(t, r.ignitionS)}</td>
                    <td className="num">{durationLabel(t, r.movingS)}</td>
                    <td className="num col-hide-sm">{durationLabel(t, r.idleS)}</td>
                    <td className="num">{num(r.distanceM / 1000, 2)}</td>
                    <td className="num col-hide-md">
                      {r.liftCount === null ? (
                        <span className="muted" title={t("reports.usage.noLiftModule")}>{t("common.empty")}</span>
                      ) : (
                        <>
                          {r.liftCount.toLocaleString(locale)}
                          {r.liftedKg !== null && <span className="muted"> · {t("reports.kg", { kg: r.liftedKg.toLocaleString(locale) })}{r.liftEstimated ? ` ${t("reports.estimated")}` : ""}</span>}
                        </>
                      )}
                    </td>
                    <td className="num" title={t(`reports.basis.${r.basis}Hint`)}>
                      {r.utilisation === null ? t("common.empty") : formatPct(r.utilisation, locale)}
                      <span className="basis-tag">{t(`reports.basis.${r.basis}Short`)}</span>
                    </td>
                    {withOperators && <td className="col-hide-md">{operatorText(t, r.operatorNames, r.operatorsHidden, r.operatorsUnknown) || t("common.empty")}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <p className="panel-footnote">{t("reports.usage.rules")}</p>
    </>
  );
}
